import React, { useState, useEffect } from 'react';
import Avatar from 'react-avatar';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Header, Icon } from 'semantic-ui-react';
import { generateResolver } from 'dyl-components';

import { UploadPhotoModal } from 'shared/modals';
import { useParams } from 'react-router';

import uploadActions from 'actions/upload';
import userActions from 'actions/user';
import FileUtils, { FILE_CATEGORIES } from 'utils/FileUtils';
import { useForm } from 'react-hook-form';
import { USER_PERMISSIONS } from 'dyl-components';
import RestrictedView from 'shared/RestrictedView';

import './index.scss';

const ProfileHeader = ({
    name,
    profilePicture,
    allowEdit,
    showUploadModal,
    onRemoveUserProfileImage,
    checkImage,
    active
}) => {
    const { id } = useParams();
    return (
        <Grid stackable className="ProfileHeader">
            <Grid.Row columns='equal' verticalAlign='middle'>
                <Grid.Column width={1}>
                    <div className={`ProfileHeader__avatar${!active ? ` ProfileHeader__avatar--inactive` : ''}`}>
                        <Avatar name={name} maxInitials={2} src={profilePicture} round size='5.625em'  />
                        <div className={'ProfileHeader__avatar-overlay'}>
                            <RestrictedView userPermission={USER_PERMISSIONS.USER_PROFILE_PREFERENCES_ADD} type="disabled" userRestrict>
                                <Icon
                                    className={`ProfileHeader__avatar-action-icon ${checkImage ? "ProfileHeader__avatar-action-icon-left" : "ProfileHeader__avatar-action-icon-center"}`}
                                    name="camera"
                                    size="big"
                                    inverted
                                    onClick={showUploadModal} />
                            </RestrictedView>
                            {checkImage &&
                                <RestrictedView userPermission={USER_PERMISSIONS.USER_PROFILE_PREFERENCES_DELETE} type="disabled" userRestrict>
                                <Icon
                                    className={"ProfileHeader__avatar-action-icon ProfileHeader__avatar-action-icon-right"}
                                    name="trash"
                                    size="big"
                                    inverted
                                    onClick={onRemoveUserProfileImage} />
                                </RestrictedView>    
                            }
                        </div>
                    </div>
                </Grid.Column>
                <Grid.Column {...(id ? { style: { paddingLeft: '2em' } } : {})}>
                    <Header className="ProfileHeader__name">
                        {name} {!active ? <span className='ProfileHeader__status'>(Inactive)</span> : ""}
                    </Header>                    
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
};

const ProfileHeaderContainer = ({user_id}) => {
    const dispatch = useDispatch();
    const {
        profilePicture,
        name,
        active
    } = useSelector((state) => {
        const { first_name = '', last_name = '', active } = state.user.userProfile;
        return ({
            profilePicture: state.user.image ? state.user.image  : '',
            name: `${first_name} ${last_name}`,
            active
        });
    })
    const [state, setState] = useState({
        newProfilePicture: '',
        isUploadPhotoModalOpen: false
    });

    const [checkImage, setImageCheck] = useState(false);
    const [isDeletable, setIsDeletable] = useState(false);

    useEffect(() => {
        const img = new Image();
        img.src = profilePicture
        img.onload = () => {
            setImageCheck(true);
            setIsDeletable(true);
        }
        img.onerror = () => {
            setImageCheck(false);
            setIsDeletable(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profilePicture])


    const [isUploading, setIsUploading] = useState(false);

    const { control, formState: { isValid }, getValues, reset } = useForm({
        mode: 'onChange',
        defaultValues: {
            attachments: []
        },
        resolver: generateResolver({
            attachments: FileUtils.getFilesizeValidator(FILE_CATEGORIES.PROFILE)
        })
    });

    const onUpload = (file) => {
        return dispatch(uploadActions.uploadProfile(file, FILE_CATEGORIES.PROFILE, "user"));
    };

    const onReadUserProfileImage = ({ user_id, thumbnail }) => {
        return dispatch(userActions.readProfileLogo({ thumbnail, user_id }));
    };

    const onRemoveUserProfileImage = ({ user_id }) => {
        return dispatch(userActions.deleteProfileLogo('', { user_id }))
    }

    const onFileChange = () => {
        setState({
            newProfilePicture: getValues("attachments").length > 0 ? getValues("attachments")[0] : null
        });
    };

    const showUploadModal = () => {
        setState({
            isUploadPhotoModalOpen: true
        });
    };

    const hideUploadModal = () => {
        setState({
            newProfilePicture: '',
            isUploadPhotoModalOpen: false
        });
        reset();
        if(!isDeletable){
            setImageCheck(false);
        }
    };

    const loggedInUser = useSelector(state => state.auth.user_id);

    const localOnUpload = async () => { 
        setIsUploading(true);
        onUpload(state.newProfilePicture)
            .then(() => {
                hideUploadModal();
                onReadUserProfileImage({ user_id, thumbnail: true });
                if (Number(loggedInUser) === Number(user_id)) {
                    dispatch(userActions.readLoggedInUserProfileLogo({ thumbnail: true, user_id }));
                }
                setIsUploading(false);
            })
    };

    const localOnRemoveUserProfileImage = async () => {
        onRemoveUserProfileImage({ user_id })
    };

    return (
        <React.Fragment>
            <ProfileHeader
                name={name}
                profilePicture={profilePicture}
                showUploadModal={showUploadModal}
                onRemoveUserProfileImage={localOnRemoveUserProfileImage}
                checkImage={checkImage}
                active={active}
            />
            <UploadPhotoModal
                onClose={hideUploadModal}
                open={state.isUploadPhotoModalOpen}
                newImage={state.newProfilePicture && URL.createObjectURL(state.newProfilePicture)}
                fileChange={onFileChange}
                onUpload={localOnUpload}

                altImage={<div className="ProfileHeader__alt-image">
                    <Avatar name={name} maxInitials={2} round size="160px" textSizeRatio={0.25} />
                </div>}
                isUploading={isUploading}
                oldImage={profilePicture}
                control={control}
                isValid={isValid}
                getValues={getValues}
                checkImage={checkImage}
                setImageCheck={setImageCheck}
            />
        </React.Fragment>
    )
}

export default ProfileHeaderContainer;
